import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { FormattedMessage, IntlProvider } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { BASE_FUNCTIONS_URL } from "../../types/firebase";
import {
  lookupLatestSecurityCodeByEmail,
  SecurityCodeLookupResult,
} from "../../data-functions/security-codes-api";
import {
  isLanguageSupported,
  messages,
  SupportedLanguages,
} from "../../languages/languages";
import { LanguageSwitcher } from "../../components/language-switcher";
import { formatDateToLocaleDateTime } from "../../utils/utils";

const getInitialLanguage = (): SupportedLanguages => {
  const browserLanguage = (navigator.language || "en").split("-")[0].toLowerCase();
  return isLanguageSupported(browserLanguage) ? (browserLanguage as SupportedLanguages) : "en";
};

const resolveLanguage = (searchParams: URLSearchParams): SupportedLanguages => {
  const searchLanguage = (searchParams.get("lang") || "").trim().toLowerCase();

  if (isLanguageSupported(searchLanguage)) {
    return searchLanguage as SupportedLanguages;
  }

  return getInitialLanguage();
};

const EMPTY_RESULT: SecurityCodeLookupResult = {
  recognized: false,
  securityCode: null,
  createdAt: null,
  message: "",
};

type GoogleVerification = {
  email: string;
};

const SecurityCodeLookupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguage] = useState<SupportedLanguages>(() => resolveLanguage(searchParams));
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [result, setResult] = useState<SecurityCodeLookupResult>(EMPTY_RESULT);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasLookup, setHasLookup] = useState(false);
  const [gisReady, setGisReady] = useState(false);
  const [googleVerification, setGoogleVerification] = useState<GoogleVerification | null>(null);
  const [googleVerificationError, setGoogleVerificationError] = useState("");
  const googleBtnRef = useRef<HTMLDivElement | null>(null);
  const localizedMessages = messages[language] as Record<string, string>;

  useEffect(() => {
    const nextLanguage = resolveLanguage(searchParams);

    if (nextLanguage !== language) {
      setLanguage(nextLanguage);
    }
  }, [language, searchParams]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      // @ts-ignore
      if (window.google?.accounts?.id) {
        setGisReady(true);
        window.clearInterval(interval);
      }
    }, 50);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!gisReady || !googleBtnRef.current || googleVerification) {
      return;
    }

    // @ts-ignore
    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
      callback: async (response: any) => {
        const idToken = response?.credential;

        if (!idToken) {
          return;
        }

        setIsLoading(true);
        setError("");
        setGoogleVerificationError("");

        try {
          const verificationResponse = await fetch(`${BASE_FUNCTIONS_URL}/public/google/verify`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_FUNCTIONS_API_KEY!,
            },
            body: JSON.stringify({ idToken }),
          });

          const verificationJson = await verificationResponse.json();

          if (verificationJson?.data?.status !== "OK" || !verificationJson?.data?.verification?.email) {
            setGoogleVerification(null);
            setGoogleVerificationError(localizedMessages.register_google_error_generic);
            setIsLoading(false);
            return;
          }

          const verifiedEmail = verificationJson.data.verification.email as string;
          setGoogleVerification({ email: verifiedEmail });
          await lookupSecurityCode(verifiedEmail);
        } catch (verificationError) {
          console.error("Google verification failed", verificationError);
          setGoogleVerification(null);
          setGoogleVerificationError(localizedMessages.register_google_error_generic);
          setIsLoading(false);
        }
      },
    });

    // @ts-ignore
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: "outline",
      size: "large",
      type: "standard",
      text: "signin_with",
      shape: "rectangular",
    });

    return () => {
      // @ts-ignore
      window.google.accounts.id.cancel?.();
      googleBtnRef.current?.replaceChildren();
    };
  }, [gisReady, googleVerification, localizedMessages]);

  const lookupSecurityCode = async (emailValue: string) => {
    setIsLoading(true);
    setError("");

    try {
      const lookupResult = await lookupLatestSecurityCodeByEmail(emailValue);
      setResult(lookupResult);
      setSubmittedEmail(emailValue);
      setHasLookup(true);
    } catch (lookupError) {
      console.error("Failed to look up security code", lookupError);
      setError(
        lookupError instanceof Error
          ? lookupError.message
          : localizedMessages.security_code_lookup_load_error,
      );
      setHasLookup(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!submittedEmail) {
      return;
    }

    await lookupSecurityCode(submittedEmail);
  };

  const resultSeverity = useMemo(() => {
    if (!hasLookup) {
      return "info" as const;
    }

    if (!result.recognized) {
      return "warning" as const;
    }

    return result.securityCode ? ("success" as const) : ("info" as const);
  }, [hasLookup, result]);

  const resultMessage = useMemo(() => {
    if (!result.recognized) {
      return localizedMessages.security_code_lookup_unrecognized;
    }

    if (result.securityCode) {
      return localizedMessages.security_code_lookup_found_message;
    }

    return localizedMessages.security_code_lookup_no_recent_code;
  }, [localizedMessages, result]);

  return (
    <IntlProvider locale={language} messages={messages[language]}>
      <Container maxWidth="sm" sx={{ marginTop: "2rem", marginBottom: "2rem" }}>
        <Card sx={{ padding: { xs: "1rem", sm: "1.5rem" } }}>
          <Stack spacing={2.5}>
            <Box display="flex" justifyContent="flex-end">
              <LanguageSwitcher
                initialLanguage={language}
                onLanguageChange={(nextLanguage) => {
                  setLanguage(nextLanguage);

                  const nextSearchParams = new URLSearchParams(searchParams);
                  nextSearchParams.set("lang", nextLanguage);
                  setSearchParams(nextSearchParams, { replace: true });
                }}
              />
            </Box>

            <Box>
              <Typography variant="h5" sx={{ textAlign: "center", marginBottom: "0.75rem" }}>
                <FormattedMessage
                  id="security_code_lookup_title"
                  defaultMessage="Latest security code"
                />
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                <FormattedMessage
                  id="security_code_lookup_description"
                  defaultMessage="Sign in with Google to check whether you have a recent security code."
                />
              </Typography>
            </Box>

            {googleVerification && (
              <Alert severity="success">
                <FormattedMessage
                  id="register_google_verified"
                  defaultMessage="Using Google verified email: {email}"
                  values={{ email: googleVerification.email }}
                />
              </Alert>
            )}

            {googleVerificationError && (
              <Alert severity="error">{googleVerificationError}</Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!googleVerification && (
              <Card variant="outlined" sx={{ padding: "1rem" }}>
                <Stack spacing={2} alignItems="center">
                  <Typography sx={{ textAlign: "center" }}>
                    <FormattedMessage
                      id="register_google_required_text"
                      defaultMessage="To proceed, please sign in with your Google account so we can verify your email address."
                    />
                  </Typography>
                  <div ref={googleBtnRef} />
                  {isLoading && <CircularProgress size={24} />}
                </Stack>
              </Card>
            )}

            {hasLookup && (
              <Card variant="outlined" sx={{ padding: "1rem" }}>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={1}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        <FormattedMessage
                          id="security_code_lookup_result_title"
                          defaultMessage="Result"
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {submittedEmail}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleRefresh}
                      disabled={isLoading}
                    >
                      <FormattedMessage
                        id="security_code_lookup_refresh"
                        defaultMessage="Refresh"
                      />
                    </Button>
                  </Box>

                  <Alert severity={resultSeverity}>
                    {resultMessage}
                  </Alert>

                  {result.securityCode ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage
                          id="security_code_lookup_code_label"
                          defaultMessage="Security code"
                        />
                      </Typography>
                      <Typography variant="h3" sx={{ letterSpacing: "0.15em", fontWeight: 700 }}>
                        {result.securityCode}
                      </Typography>
                      {result.createdAt && (
                        <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                          <FormattedMessage
                            id="security_code_lookup_created_at"
                            defaultMessage="Received: {time}"
                            values={{
                              time: formatDateToLocaleDateTime(new Date(result.createdAt)),
                            }}
                          />
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      <FormattedMessage
                        id="security_code_lookup_not_found_detail"
                        defaultMessage="If a new code arrives, use Refresh to check again."
                      />
                    </Typography>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
      </Container>
    </IntlProvider>
  );
};

export default SecurityCodeLookupPage;
